// src/services/firebase/firestoreService.js
import { db, storage } from './config';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  getDocs,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  deleteDoc,
  writeBatch,
  getCountFromServer,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

// ===== INDIA TIMEZONE HELPERS =====

const INDIA_OFFSET = 5.5 * 60 * 60 * 1000;

export const getIndiaDate = () => {
  const now = new Date();
  const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
  return new Date(utc + INDIA_OFFSET);
};

export const getIndiaDateString = (date = null) => {
  const d = date || getIndiaDate();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isSunday = (dateStr) => {
  const d = new Date(dateStr);
  return d.getDay() === 0;
};

const addDays = (dateStr, days) => {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return getIndiaDateString(d);
};

// ===== USER FUNCTIONS =====

export const getUserData = async (userId) => {
  const userDoc = await getDoc(doc(db, 'users', userId));
  if (userDoc.exists()) {
    return { id: userDoc.id, ...userDoc.data() };
  }
  return null;
};

export const updateUserData = async (userId, data) => {
  await updateDoc(doc(db, 'users', userId), {
    ...data,
    lastActive: serverTimestamp()
  });
};

// ===== STREAK LOGIC =====

export const recalculateStreak = async (userId) => {
  const userSnap = await getDoc(doc(db, 'users', userId));
  if (!userSnap.exists()) return 0;
  const userData = userSnap.data();

  const lastDate = userData.lastCompletedDate;
  if (!lastDate) return userData.streakCount || 0;

  const todayStr = getIndiaDateString();
  const pausedDates = userData.streakPausedDates || [];

  if (lastDate === todayStr) return userData.streakCount || 0;

  const fitnessDoneToday = await getDoc(doc(db, 'fitnessDailyCompletions', `${userId}_${todayStr}`));
  const lekhiDoneToday = await getDoc(doc(db, 'lekhiDailyCompletions', `${userId}_${todayStr}`));
  const todayAlreadyDone = (fitnessDoneToday.exists() && fitnessDoneToday.data()?.allTasksCompleted) ||
                           lekhiDoneToday.exists();

  if (todayAlreadyDone) return userData.streakCount || 0;

  let checkDate = addDays(lastDate, 1);
  while (checkDate < todayStr) {
    if (isSunday(checkDate) || pausedDates.includes(checkDate)) {
      checkDate = addDays(checkDate, 1);
      continue;
    }
    const newStreak = 0;
    if (newStreak !== (userData.streakCount || 0)) {
      const totalPoints = (userData.fitnessPoints || 0) + (userData.lekhiPoints || 0);
      await updateDoc(doc(db, 'users', userId), {
        streakCount: newStreak,
        rankScore: totalPoints
      });
    }
    return newStreak;
  }

  return userData.streakCount || 0;
};

export const calculateCurrentStreak = (lastCompletedDate, storedStreak) => {
  if (!lastCompletedDate) return 0;
  return storedStreak || 0;
};

export const validateAndSyncStreak = async (userId) => {
  return await recalculateStreak(userId);
};

const recordDailyActivity = async (userId, source) => {
  const todayStr = getIndiaDateString();
  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);
  if (!userSnap.exists()) return { streakIncremented: false, newStreak: 0 };
  const userData = userSnap.data();

  const lastDate = userData.lastCompletedDate;
  const pausedDates = userData.streakPausedDates || [];
  const currentStreak = userData.streakCount || 0;
  const totalPoints = (userData.fitnessPoints || 0) + (userData.lekhiPoints || 0);

  if (lastDate === todayStr) {
    // Streak doesn't change, but points may have just changed (e.g. the
    // user's second completion of the day) — keep rankScore in sync.
    await updateDoc(userRef, {
      rankScore: currentStreak * 1000000 + totalPoints
    });
    return { streakIncremented: false, newStreak: currentStreak };
  }

  let newStreak;
  if (!lastDate) {
    newStreak = 1;
  } else {
    let validContinuity = true;
    let checkDate = addDays(lastDate, 1);
    while (checkDate < todayStr) {
      if (!isSunday(checkDate) && !pausedDates.includes(checkDate)) {
        validContinuity = false;
        break;
      }
      checkDate = addDays(checkDate, 1);
    }
    newStreak = validContinuity ? currentStreak + 1 : 1;
  }

  await updateDoc(userRef, {
    streakCount: newStreak,
    lastCompletedDate: todayStr,
    lastActive: serverTimestamp(),
    rankScore: newStreak * 1000000 + totalPoints
  });

  await updateAppStatsForToday(userId);

  return { streakIncremented: true, newStreak };
};

const updateAppStatsForToday = async (userId) => {
  const todayStr = getIndiaDateString();
  const fitnessSnap = await getDoc(doc(db, 'fitnessDailyCompletions', `${userId}_${todayStr}`));
  const lekhiSnap = await getDoc(doc(db, 'lekhiDailyCompletions', `${userId}_${todayStr}`));
  const fitnessDone = fitnessSnap.exists() && fitnessSnap.data()?.allTasksCompleted;
  const lekhiDone = lekhiSnap.exists();

  const completionsCount = (fitnessDone ? 1 : 0) + (lekhiDone ? 1 : 0);
  if (completionsCount !== 1) return;

  const statsRef = doc(db, 'appStats', 'daily');
  const statsSnap = await getDoc(statsRef);
  if (statsSnap.exists() && statsSnap.data().date === todayStr) {
    await updateDoc(statsRef, {
      yesterdayCount: statsSnap.data().yesterdayCount || 0,
      yesterdayDate: statsSnap.data().yesterdayDate || '',
      count: (statsSnap.data().count || 0) + 1
    });
  } else {
    const previousCount = statsSnap.exists() ? (statsSnap.data().count || 0) : 0;
    const previousDate = statsSnap.exists() ? (statsSnap.data().date || '') : '';
    await setDoc(statsRef, {
      date: todayStr,
      count: 1,
      yesterdayDate: previousDate,
      yesterdayCount: previousCount
    });
  }
};

const updateVillagePointsForUser = async (village, district, amount = 1) => {
  if (!village || !amount) return;
  const villageSlug = village.toLowerCase().replace(/\s+/g, '_');
  const villageRef = doc(db, 'villages', villageSlug);
  const villageSnap = await getDoc(villageRef);
  if (villageSnap.exists()) {
    await updateDoc(villageRef, {
      totalPoints: (villageSnap.data().totalPoints || 0) + amount
    });
  } else {
    await setDoc(villageRef, {
      name: village,
      district: district || '',
      totalPoints: amount,
      createdAt: serverTimestamp()
    });
  }
};

// ===== VILLAGE FUNCTIONS =====

export const getVillageRankings = async () => {
  const snapshot = await getDocs(collection(db, 'villages'));
  const villages = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  return villages
    .sort((a, b) => (b.totalPoints || 0) - (a.totalPoints || 0))
    .map((village, index) => ({
      village: village.name,
      points: village.totalPoints || 0,
      rank: index + 1
    }));
};


export const getStreakLeaderboard = async (currentUserId, currentUserRankScore) => {
  const topQuery = query(collection(db, 'users'), orderBy('rankScore', 'desc'), limit(5));
  const topSnap = await getDocs(topQuery);
  const topDocs = topSnap.docs.map(d => ({ id: d.id, ...d.data() }));

  if (topDocs.length === 0) {
    return { list: [], currentUserRank: null, currentUserInList: false };
  }

  const boundaryScore = topDocs[topDocs.length - 1].rankScore;
  const strictlyAbove = topDocs.filter(d => d.rankScore > boundaryScore);

  const tieSnap = await getDocs(query(collection(db, 'users'), where('rankScore', '==', boundaryScore)));
  const tiedAtBoundary = tieSnap.docs.map(d => ({ id: d.id, ...d.data() }));

  // Daily-rotating tiebreak so no name is ever permanently stuck at the bottom of a tie.
  const todayStr = getIndiaDateString();
  const tieBreakValue = (u) => {
    const str = `${u.id}_${todayStr}`;
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = (hash * 31 + str.charCodeAt(i)) | 0;
    }
    return hash;
  };
  tiedAtBoundary.sort((a, b) => tieBreakValue(a) - tieBreakValue(b));

  const list = [...strictlyAbove, ...tiedAtBoundary];
  const currentUserInList = list.some(u => u.id === currentUserId);

  let currentUserRank = null;
  if (!currentUserInList && currentUserRankScore !== undefined && currentUserRankScore !== null) {
    const higherCountSnap = await getCountFromServer(
      query(collection(db, 'users'), where('rankScore', '>', currentUserRankScore))
    );
    currentUserRank = higherCountSnap.data().count + 1;
  }

  return { list, currentUserRank, currentUserInList };
};


export const backfillRankScores = async () => {
  const users = await getAllUsers();
  const targets = users.filter(u => u.role !== 'admin');
  let updated = 0;
  for (const u of targets) {
    const totalPoints = (u.fitnessPoints || 0) + (u.lekhiPoints || 0);
    const streak = u.streakCount || 0;
    const rankScore = streak * 1000000 + totalPoints;
    if (u.rankScore !== rankScore) {
      await updateDoc(doc(db, 'users', u.id), { rankScore });
      updated++;
    }
  }
  return { total: targets.length, updated };
};

// ===== COMPLETION FUNCTIONS =====

export const getWeekCompletions = async (userId) => {
  const india = getIndiaDate();
  const dayOfWeek = india.getDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;

  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(india);
    date.setDate(india.getDate() + mondayOffset + i);
    return getIndiaDateString(date);
  });

  const fitnessResults = await Promise.all(
    weekDates.map(dateStr =>
      getDoc(doc(db, 'fitnessDailyCompletions', `${userId}_${dateStr}`))
    )
  );
  const lekhiResults = await Promise.all(
    weekDates.map(dateStr =>
      getDoc(doc(db, 'lekhiDailyCompletions', `${userId}_${dateStr}`))
    )
  );

  // Sunday tests write only to lekhiTestResults, so check those too.
  const testDates = new Set();
  try {
    const testSnap = await getDocs(
      query(collection(db, 'lekhiTestResults'), where('userId', '==', userId))
    );
    testSnap.docs.forEach(d => {
      const submittedAt = d.data().submittedAt;
      if (!submittedAt) return;
      const jsDate = submittedAt.seconds
        ? new Date(submittedAt.seconds * 1000)
        : new Date(submittedAt);
      if (isNaN(jsDate)) return;
      const indiaDate = new Date(
        jsDate.getTime() + (jsDate.getTimezoneOffset() * 60000) + INDIA_OFFSET
      );
      testDates.add(getIndiaDateString(indiaDate));
    });
  } catch (e) {
    // If this read fails, fall back to fitness/lekhi only.
  }

  const completions = {};
  weekDates.forEach((dateStr, i) => {
    const fitDone = fitnessResults[i].exists() && fitnessResults[i].data()?.allTasksCompleted === true;
    const lekDone = lekhiResults[i].exists();
    const testDone = testDates.has(dateStr);
    completions[dateStr] = fitDone || lekDone || testDone;
  });

  return completions;
};

export const getLastDaysCompletions = async (userId, numDays) => {
  const india = getIndiaDate();
  const lookbackWindow = 7;
  const dates = [];
  for (let i = lookbackWindow; i >= 1; i--) {
    const date = new Date(india);
    date.setDate(india.getDate() - i);
    dates.push(getIndiaDateString(date));
  }

  const fitnessResults = await Promise.all(
    dates.map(dateStr =>
      getDoc(doc(db, 'fitnessDailyCompletions', `${userId}_${dateStr}`))
    )
  );
  const lekhiResults = await Promise.all(
    dates.map(dateStr =>
      getDoc(doc(db, 'lekhiDailyCompletions', `${userId}_${dateStr}`))
    )
  );

  const completedDays = dates
    .map((date, i) => ({
      date,
      fitnessDone: fitnessResults[i].exists() && fitnessResults[i].data()?.allTasksCompleted === true,
      lekhiDone: lekhiResults[i].exists()
    }))
    .filter(d => d.fitnessDone || d.lekhiDone);

  return completedDays.slice(-numDays);
};

export const getTodayCompletion = async (userId) => {
  const todayStr = getIndiaDateString();
  const docSnap = await getDoc(doc(db, 'fitnessDailyCompletions', `${userId}_${todayStr}`));
  if (docSnap.exists()) return { id: docSnap.id, ...docSnap.data() };
  return null;
};

export const getTodayLekhiCompletion = async (userId) => {
  const todayStr = getIndiaDateString();
  const docSnap = await getDoc(doc(db, 'lekhiDailyCompletions', `${userId}_${todayStr}`));
  if (docSnap.exists()) return { id: docSnap.id, ...docSnap.data() };
  return null;
};

export const saveTaskCompletion = async (userId, taskId, currentDay) => {
  const todayStr = getIndiaDateString();
  const completionId = `${userId}_${todayStr}`;
  const completionRef = doc(db, 'fitnessDailyCompletions', completionId);

  const existing = await getDoc(completionRef);
  const existingData = existing.exists() ? existing.data() : null;

  const completedTasks = existingData?.completedTasks || {};
  completedTasks[taskId] = { completed: true, completedAt: serverTimestamp() };

  await setDoc(completionRef, {
    userId,
    date: todayStr,
    currentDay,
    completedTasks,
    allTasksCompleted: existingData?.allTasksCompleted === true,
    updatedAt: serverTimestamp(),
    ...(existingData ? {} : { createdAt: serverTimestamp() })
  });
};

export const checkAndUpdateDayCompletion = async (userId, currentDay, totalTasks) => {
  const todayStr = getIndiaDateString();
  const completionId = `${userId}_${todayStr}`;
  const completionRef = doc(db, 'fitnessDailyCompletions', completionId);

  const completionSnap = await getDoc(completionRef);
  if (!completionSnap.exists()) return false;

  const completionData = completionSnap.data();
  const completedCount = Object.keys(completionData.completedTasks || {}).length;

  if (completedCount < totalTasks) return false;
  if (completionData.allTasksCompleted) return false;

  await updateDoc(completionRef, {
    allTasksCompleted: true,
    dayCompletedAt: serverTimestamp()
  });

  const userSnap = await getDoc(doc(db, 'users', userId));
  if (!userSnap.exists()) return false;
  const userData = userSnap.data();

  // Today's fitness day is already recorded — award nothing.
  if (userData.lastFitnessCompletedDate === todayStr) return false;

  const newPoints = (userData.fitnessPoints || 0) + 1;
  const newDay = currentDay + 1;

  await updateDoc(doc(db, 'users', userId), {
    fitnessPoints: newPoints,
    fitnessCurrentDay: newDay,
    lastFitnessCompletedDate: todayStr
  });

  await updateVillagePointsForUser(userData.village, userData.district, 1);

  await recordDailyActivity(userId, 'fitness');

  return true;
};

export const getYesterdayCompletionCount = async () => {
  try {
    const yesterday = new Date(getIndiaDate());
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = getIndiaDateString(yesterday);
    const statsSnap = await getDoc(doc(db, 'appStats', 'daily'));
    if (!statsSnap.exists()) return 0;
    const data = statsSnap.data();
    if (data.date === yesterdayStr) return data.count || 0;
    if (data.yesterdayDate === yesterdayStr) return data.yesterdayCount || 0;
    return 0;
  } catch (e) {
    return 0;
  }
};

// ===== DAILY PLAN FUNCTIONS =====

export const getDailyPlan = async (dayOfWeek, weekNumber, category) => {
  const dayMap = {
    0: 'Sun', 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat'
  };
  const categoryMap = {
    beginnerBoys: 'BB', beginnerGirls: 'BG',
    advancedBoys: 'AB', advancedGirls: 'AG',
    specialBoys: 'SB', specialGirls: 'SG'
  };

  const dayCode = dayMap[dayOfWeek];
  const categoryCode = categoryMap[category] || category.slice(0, 2);
  const planId = `${dayCode}W${weekNumber}${categoryCode}`;

  const planSnap = await getDoc(doc(db, 'fitnessDailyPlans', planId));
  if (planSnap.exists()) return { id: planSnap.id, ...planSnap.data() };

  for (let w = weekNumber - 1; w >= 1; w--) {
    const fallbackId = `${dayCode}W${w}${categoryCode}`;
    const fallbackSnap = await getDoc(doc(db, 'fitnessDailyPlans', fallbackId));
    if (fallbackSnap.exists()) return { id: fallbackSnap.id, ...fallbackSnap.data() };
  }

  return null;
};

export const getTask = async (taskId) => {
  const taskSnap = await getDoc(doc(db, 'fitnessTasks', taskId));
  if (taskSnap.exists()) return { id: taskSnap.id, ...taskSnap.data() };
  return null;
};

export const getDailyTasksForUser = async (category, currentDay, userData = {}) => {
  const india = getIndiaDate();
  const dayOfWeek = india.getDay();

  const weekNumber = Math.ceil(currentDay / 7);

  const plan = await getDailyPlan(dayOfWeek, weekNumber, category);
  if (!plan || !plan.tasks || plan.tasks.length === 0) {
    return { tasks: [], showWhatsAppSupport: true };
  }

  const taskPromises = plan.tasks.map(async (taskPlan) => {
    const task = await getTask(taskPlan.taskId);
    if (!task) return null;
    return {
      id: task.id,
      name: task.name,
      type: task.type,
      hasMediaPipe: task.hasMediaPipe || false,
      mediaPipeType: task.mediaPipeType || null,
      reps: taskPlan.reps || null,
      sets: taskPlan.sets || null,
      restTime: taskPlan.restTime || null,
      comment: taskPlan.comment || null,
      youtubeURL: task.youtubeURL || null,
      icon: task.type === 'strength' ? '💪' : task.type === 'athletics' ? '🏃' : '🥗'
    };
  });

  const tasks = (await Promise.all(taskPromises)).filter(Boolean);
  return { tasks, showWhatsAppSupport: false };
};

// ===== CAMP FUNCTIONS =====

export const getCampPlan = async (campNumber) => {
  const campSnap = await getDoc(doc(db, 'campPlans', `camp${campNumber}`));
  if (!campSnap.exists()) return null;

  const campData = campSnap.data();
  const taskPromises = (campData.tasks || []).map(async (taskPlan) => {
    const task = await getTask(taskPlan.taskId);
    if (!task) return null;
    return {
      id: task.id,
      name: task.name,
      type: task.type,
      hasMediaPipe: task.hasMediaPipe || false,
      mediaPipeType: task.mediaPipeType || null,
      reps: taskPlan.reps || null,
      sets: taskPlan.sets || null,
      restTime: taskPlan.restTime || null,
      comment: taskPlan.comment || null,
      youtubeURL: task.youtubeURL || null,
      icon: task.type === 'strength' ? '💪' : task.type === 'athletics' ? '🏃' : '🥗'
    };
  });

  const tasks = (await Promise.all(taskPromises)).filter(Boolean);
  return { campNumber, tasks };
};

// ===== ACHIEVEMENT FUNCTIONS =====

export const getAchievementDefinitions = async () => {
  const snapshot = await getDocs(collection(db, 'achievements'));
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getUserAchievements = async (userId) => {
  const snapshot = await getDocs(collection(db, 'userAchievements'));
  const userAchievements = snapshot.docs
    .filter(d => d.data().userId === userId)
    .map(d => ({ id: d.id, ...d.data() }));
  return userAchievements;
};

export const unlockAchievement = async (userId, achievementId) => {
  const docId = `${userId}_${achievementId}`;
  const ref = doc(db, 'userAchievements', docId);
  const existing = await getDoc(ref);
  if (existing.exists()) return false;
  await setDoc(ref, {
    userId,
    achievementId,
    unlockedAt: serverTimestamp()
  });
  return true;
};

export const checkAndUnlockAchievements = async (userId) => {
  try {
    const [userSnap, definitions, userAchievements] = await Promise.all([
      getDoc(doc(db, 'users', userId)),
      getAchievementDefinitions(),
      getUserAchievements(userId)
    ]);

    if (!userSnap.exists()) return [];
    const userData = userSnap.data();

    const unlockedIds = new Set(userAchievements.map(a => a.achievementId));
    const newlyUnlocked = [];

    // Lazy-load data only when needed
    let userStats = null;
    let lekhiCompletions = null;
    let fitnessCompletions = null;
    let lekhiActivity = null;
    let testResults = null;
    let villageData = null;

    const loadUserStats = async () => {
      if (userStats === null) {
        const s = await getDoc(doc(db, 'fitnessUserStats', userId));
        userStats = s.exists() ? s.data() : {};
      }
      return userStats;
    };

    const loadLekhiCompletions = async () => {
      if (lekhiCompletions === null) {
        const q = query(collection(db, 'lekhiDailyCompletions'), where('userId', '==', userId));
        const snap = await getDocs(q);
        lekhiCompletions = snap.docs.map(d => d.data().date).filter(Boolean);
      }
      return lekhiCompletions;
    };

    const loadFitnessCompletions = async () => {
      if (fitnessCompletions === null) {
        const q = query(collection(db, 'fitnessDailyCompletions'),
          where('userId', '==', userId),
          where('allTasksCompleted', '==', true));
        const snap = await getDocs(q);
        fitnessCompletions = snap.docs.map(d => d.data().date).filter(Boolean);
      }
      return fitnessCompletions;
    };

    const loadLekhiActivity = async () => {
      if (lekhiActivity === null) {
        const q = query(collection(db, 'lekhiDailyActivity'),
          where('userId', '==', userId),
          where('completed', '==', true));
        const snap = await getDocs(q);
        lekhiActivity = snap.docs.map(d => d.data());
      }
      return lekhiActivity;
    };

    const loadTestResults = async () => {
      if (testResults === null) {
        const q = query(collection(db, 'lekhiTestResults'), where('userId', '==', userId));
        const snap = await getDocs(q);
        testResults = snap.docs.map(d => d.data());
      }
      return testResults;
    };

    const loadVillageData = async () => {
      if (villageData === null) {
        const village = userData.village || '';
        if (!village) { villageData = {}; return villageData; }
        const slug = village.toLowerCase().replace(/\s+/g, '_');
        const vSnap = await getDoc(doc(db, 'villages', slug));
        villageData = vSnap.exists() ? vSnap.data() : {};
      }
      return villageData;
    };

    const countTopicsBySubject = (activity, subject) => {
      const set = new Set();
      activity.forEach(a => {
        if (a.subjectCode === subject) set.add(a.topicCode);
      });
      return set.size;
    };

    const countTotalUniqueTopics = (activity) => {
      const set = new Set();
      activity.forEach(a => set.add(`${a.subjectCode}|${a.topicCode}`));
      return set.size;
    };

    const countDualDays = (fitDates, lekhiDates) => {
      const fitSet = new Set(fitDates);
      let count = 0;
      lekhiDates.forEach(d => { if (fitSet.has(d)) count++; });
      return count;
    };

    for (const achievement of definitions) {
      if (unlockedIds.has(achievement.id)) continue;
      if (achievement.active === false) continue;

      const { criteriaType, criteriaValue } = achievement;
      let met = false;

      try {
        // KHELADU
        if (criteriaType === 'totalFitnessDays') {
          const fitDates = await loadFitnessCompletions();
          met = fitDates.length >= criteriaValue;
        } else if (criteriaType === 'totalSquats') {
          const stats = await loadUserStats();
          met = (stats.totalSquats || 0) >= criteriaValue;
        } else if (criteriaType === 'totalJumpingJacks') {
          const stats = await loadUserStats();
          met = (stats.totalJumpingJacks || 0) >= criteriaValue;
        }

        // VIDYARTHI
        else if (criteriaType === 'totalLekhiDays') {
          const lekhiDates = await loadLekhiCompletions();
          met = lekhiDates.length >= criteriaValue;
        } else if (criteriaType === 'totalTopicsCovered') {
          const activity = await loadLekhiActivity();
          met = countTotalUniqueTopics(activity) >= criteriaValue;
        } else if (criteriaType === 'grammarTopicsCovered') {
          const activity = await loadLekhiActivity();
          met = countTopicsBySubject(activity, 'grammar') >= criteriaValue;
        } else if (criteriaType === 'mathTopicsCovered') {
          const activity = await loadLekhiActivity();
          met = countTopicsBySubject(activity, 'math') >= criteriaValue;
        } else if (criteriaType === 'reasoningTopicsCovered') {
          const activity = await loadLekhiActivity();
          met = countTopicsBySubject(activity, 'reasoning') >= criteriaValue;
        } else if (criteriaType === 'gkTopicsCovered') {
          const activity = await loadLekhiActivity();
          met = countTopicsBySubject(activity, 'gk') >= criteriaValue;
        } else if (criteriaType === 'testsCompleted') {
          const tests = await loadTestResults();
          met = tests.length >= criteriaValue;
        } else if (criteriaType === 'highestTestScore') {
          const tests = await loadTestResults();
          const best = Math.max(0, ...tests.map(t => t.score || 0));
          met = best >= criteriaValue;
        }

        // TAPASVI
        else if (criteriaType === 'unifiedStreak') {
          met = (userData.streakCount || 0) >= criteriaValue;
        } else if (criteriaType === 'dualDays') {
          const [fitDates, lekhiDates] = await Promise.all([
            loadFitnessCompletions(),
            loadLekhiCompletions()
          ]);
          met = countDualDays(fitDates, lekhiDates) >= criteriaValue;
        }
        else if (criteriaType === 'campsCompleted') {
          const attendedCamps = userData.attendedCamps || [];
          met = attendedCamps.length >= criteriaValue;
        }
      
        // GAAVKARI
        else if (criteriaType === 'villageTotalPoints') {
          const v = await loadVillageData();
          met = (v.totalPoints || 0) >= criteriaValue;
        } else if (criteriaType === 'userPointsContributed') {
          met = ((userData.fitnessPoints || 0) + (userData.lekhiPoints || 0)) >= criteriaValue;
        }

      } catch (e) {
        // Skip this achievement if data fetch fails, continue with others
        continue;
      }

      if (met) {
        const wasNew = await unlockAchievement(userId, achievement.id);
        if (wasNew) newlyUnlocked.push(achievement);
      }
    }

    return newlyUnlocked;
  } catch (e) {
    return [];
  }
};

export const checkVillageAchievements = async (userId, villageName) => {
  try {
    const todayStr = getIndiaDateString();
    const lastCheckKey = `lastVillageAchievementCheck_${userId}`;
    const lastCheck = localStorage.getItem(lastCheckKey);
    if (lastCheck === todayStr) return [];

    localStorage.setItem(lastCheckKey, todayStr);

    const villageSlug = villageName.toLowerCase().replace(/\s+/g, '_');
    const [villageSnap, definitions, userAchievements] = await Promise.all([
      getDoc(doc(db, 'villages', villageSlug)),
      getAchievementDefinitions(),
      getUserAchievements(userId)
    ]);

    if (!villageSnap.exists()) return [];
    const villageData = villageSnap.data();
    const unlockedIds = new Set(userAchievements.map(a => a.achievementId));
    const newlyUnlocked = [];

    const villageAchievements = definitions.filter(a => a.criteriaType === 'villagePoints');

    for (const achievement of villageAchievements) {
      if (unlockedIds.has(achievement.id)) continue;
      const met = (villageData.totalPoints || 0) >= achievement.criteriaValue;
      if (met) {
        const wasNew = await unlockAchievement(userId, achievement.id);
        if (wasNew) newlyUnlocked.push(achievement);
      }
    }

    return newlyUnlocked;
  } catch (e) {
    return [];
  }
};

// ===== CAMP COMPLETION FUNCTIONS =====

export const getTodayCampCompletion = async (userId, campNumber) => {
  const todayStr = getIndiaDateString();
  const docId = `${userId}_camp${campNumber}_${todayStr}`;
  const docSnap = await getDoc(doc(db, 'campCompletions', docId));
  if (docSnap.exists()) return { id: docSnap.id, ...docSnap.data() };
  return null;
};

export const saveCampTaskCompletion = async (userId, campNumber, taskId) => {
  const todayStr = getIndiaDateString();
  const docId = `${userId}_camp${campNumber}_${todayStr}`;
  const completionRef = doc(db, 'campCompletions', docId);
  const existing = await getDoc(completionRef);
  const existingData = existing.exists() ? existing.data() : null;
  const completedTasks = existingData?.completedTasks || {};
  completedTasks[taskId] = { completed: true, completedAt: serverTimestamp() };
  await setDoc(completionRef, {
    userId,
    campNumber,
    date: todayStr,
    completedTasks,
    allTasksCompleted: false,
    updatedAt: serverTimestamp(),
    ...(existingData ? {} : { createdAt: serverTimestamp() })
  });
};

export const checkAndUpdateCampCompletion = async (userId, campNumber, totalTasks) => {
  const todayStr = getIndiaDateString();
  const docId = `${userId}_camp${campNumber}_${todayStr}`;
  const completionRef = doc(db, 'campCompletions', docId);
  const completionSnap = await getDoc(completionRef);
  if (!completionSnap.exists()) return false;
  const completionData = completionSnap.data();
  const completedCount = Object.keys(completionData.completedTasks || {}).length;
  if (completedCount < totalTasks) return false;
  if (completionData.allTasksCompleted) return false;
  await updateDoc(completionRef, {
    allTasksCompleted: true,
    campCompletedAt: serverTimestamp()
  });
  const userSnap = await getDoc(doc(db, 'users', userId));
  if (!userSnap.exists()) return false;
  const userData = userSnap.data();
  const attendedCamps = userData.attendedCamps || [];
  if (!attendedCamps.includes(campNumber)) {
    await updateDoc(doc(db, 'users', userId), {
      attendedCamps: [...attendedCamps, campNumber],
      lastActive: serverTimestamp()
    });
  }
  await checkAndUnlockAchievements(userId);
  return true;
};

// ===== ADMIN FUNCTIONS =====

export const getAllUsers = async () => {
  const snapshot = await getDocs(collection(db, 'users'));
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const createUser = async (userId, userData) => {
  await setDoc(doc(db, 'users', userId), {
    ...userData,
    createdAt: serverTimestamp(),
    lastActive: serverTimestamp(),
    notificationPromptStatus: 'never_asked',
    rankScore: 0
  });
  if (userData.village) {
    const villageSlug = userData.village.toLowerCase().replace(/\s+/g, '_');
    const villageRef = doc(db, 'villages', villageSlug);
    const villageSnap = await getDoc(villageRef);
    if (!villageSnap.exists()) {
      await setDoc(villageRef, {
        name: userData.village,
        district: userData.district || '',
        totalPoints: 0,
        createdAt: serverTimestamp()
      });
    }
  }
};

export const updateUser = async (userId, data) => {
  await updateDoc(doc(db, 'users', userId), data);
};

export const getAllTasks = async () => {
  const snapshot = await getDocs(collection(db, 'fitnessTasks'));
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const createTask = async (taskData) => {
  const taskRef = doc(collection(db, 'fitnessTasks'));
  await setDoc(taskRef, { ...taskData, createdAt: serverTimestamp() });
  return taskRef.id;
};

export const getWeeklySchedule = async () => {
  const docSnap = await getDoc(doc(db, 'weeklySchedule', 'main'));
  if (docSnap.exists()) return docSnap.data();
  return null;
};

export const saveWeeklySchedule = async (scheduleData) => {
  await setDoc(doc(db, 'weeklySchedule', 'main'), {
    ...scheduleData,
    updatedAt: serverTimestamp()
  });
};

export const getAllDailyPlans = async () => {
  const snapshot = await getDocs(collection(db, 'fitnessDailyPlans'));
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const deleteDailyPlansForDayWeek = async (day, week) => {
  const dayMap = {
    monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed',
    thursday: 'Thu', friday: 'Fri', saturday: 'Sat'
  };
  const categoryMap = {
    beginnerBoys: 'BB', beginnerGirls: 'BG',
    advancedBoys: 'AB', advancedGirls: 'AG',
    specialBoys: 'SB', specialGirls: 'SG'
  };
  const dayCode = dayMap[day];
  const deletions = Object.values(categoryMap).map(catCode => {
    const planId = `${dayCode}W${week}${catCode}`;
    return deleteDoc(doc(db, 'fitnessDailyPlans', planId));
  });
  await Promise.all(deletions);
};

export const saveDailyPlan = async (day, week, level, tasks) => {
  const dayMap = {
    monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed',
    thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun'
  };
  const categoryMap = {
    beginnerBoys: 'BB', beginnerGirls: 'BG',
    advancedBoys: 'AB', advancedGirls: 'AG',
    specialBoys: 'SB', specialGirls: 'SG'
  };
  const planId = `${dayMap[day]}W${week}${categoryMap[level]}`;
  await setDoc(doc(db, 'fitnessDailyPlans', planId), {
    day, week, level, tasks,
    updatedAt: serverTimestamp()
  });
  return planId;
};

export const getAllCampPlans = async () => {
  const snapshot = await getDocs(collection(db, 'campPlans'));
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const saveCampPlan = async (campNumber, tasks) => {
  await setDoc(doc(db, 'campPlans', `camp${campNumber}`), {
    campNumber,
    tasks,
    updatedAt: serverTimestamp()
  });
};

export const unlockCampForUser = async (userId, campNumber) => {
  const userSnap = await getDoc(doc(db, 'users', userId));
  if (!userSnap.exists()) return;
  const userData = userSnap.data();
  const unlockedCamps = userData.unlockedCamps || [];
  if (!unlockedCamps.includes(campNumber)) {
    await updateDoc(doc(db, 'users', userId), {
      unlockedCamps: [...unlockedCamps, campNumber]
    });
  }
};

export const bulkUnlockCamp = async (userIds, campNumber) => {
  await Promise.all(userIds.map(userId => unlockCampForUser(userId, campNumber)));
};

export const getCampCompletionSummary = async () => {
  const users = await getAllUsers();
  return users
    .filter(u => u.role !== 'admin')
    .map(user => ({
      userId: user.id,
      name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || '—',
      village: user.village,
      currentCampUnlocked: (user.unlockedCamps || []).length > 0
        ? Math.max(...user.unlockedCamps)
        : null,
      camp1: (user.attendedCamps || []).includes(1) ? 'Yes' : 'No',
      camp2: (user.attendedCamps || []).includes(2) ? 'Yes' : 'No',
      camp3: (user.attendedCamps || []).includes(3) ? 'Yes' : 'No',
      camp4: (user.attendedCamps || []).includes(4) ? 'Yes' : 'No',
      camp5: (user.attendedCamps || []).includes(5) ? 'Yes' : 'No',
    }));
};

export const getAdminStats = async () => {
  const yesterday = new Date(getIndiaDate().getTime() - 24 * 60 * 60 * 1000);
  const yesterdayStr = getIndiaDateString(yesterday);
  const threeDaysAgo = new Date(getIndiaDate().getTime() - 3 * 24 * 60 * 60 * 1000);

  const users = await getAllUsers();
  const nonAdmin = users.filter(u => u.role !== 'admin');
  const activeUsers = nonAdmin.filter(u => u.status !== 'inactive');

  const atRisk = activeUsers.filter(u => {
    if (!u.lastActive) return true;
    const last = u.lastActive.seconds
      ? new Date(u.lastActive.seconds * 1000)
      : new Date(u.lastActive);
    return last < threeDaysAgo;
  });

  const villages = new Set(nonAdmin.map(u => u.village).filter(Boolean));

  let yesterdayCompletions = 0;
  if (activeUsers.length > 0) {
    const fitnessResults = await Promise.all(
      activeUsers.map(u =>
        getDoc(doc(db, 'fitnessDailyCompletions', `${u.id}_${yesterdayStr}`))
      )
    );
    const lekhiResults = await Promise.all(
      activeUsers.map(u =>
        getDoc(doc(db, 'lekhiDailyCompletions', `${u.id}_${yesterdayStr}`))
      )
    );
    yesterdayCompletions = activeUsers.filter((_, i) => {
      const fitDone = fitnessResults[i].exists() && fitnessResults[i].data()?.allTasksCompleted;
      const lekDone = lekhiResults[i].exists();
      return fitDone || lekDone;
    }).length;
  }

  const yesterdayRate = activeUsers.length > 0
    ? Math.round((yesterdayCompletions / activeUsers.length) * 100)
    : 0;

  const totalPoints = nonAdmin.reduce(
    (sum, u) => sum + (u.fitnessPoints || 0) + (u.lekhiPoints || 0), 0
  );

  const streakUsers = activeUsers.filter(u => (u.streakCount || 0) >= 1);
  const avgStreak = streakUsers.length > 0
    ? Math.round(streakUsers.reduce((sum, u) => sum + (u.streakCount || 0), 0) / streakUsers.length)
    : 0;

  const notifSubscribed = activeUsers.filter(u => u.notificationPromptStatus === 'subscribed');
  const notifRate = activeUsers.length > 0
    ? Math.round((notifSubscribed.length / activeUsers.length) * 100)
    : 0;

  const day1Completed = activeUsers.filter(u => (u.fitnessCurrentDay || 1) > 1);
  const day1Rate = activeUsers.length > 0
    ? Math.round((day1Completed.length / activeUsers.length) * 100)
    : 0;

  return {
    totalUsers: nonAdmin.length,
    activeUsers: activeUsers.length,
    atRiskCount: atRisk.length,
    yesterdayRate,
    villageCount: villages.size,
    totalPoints,
    avgStreak,
    streakUsersCount: streakUsers.length,
    notifRate,
    day1Rate
  };
};

export const getFitnessVsLekhiDayTotals = async () => {
  const fitnessQuery = query(
    collection(db, 'fitnessDailyCompletions'),
    where('allTasksCompleted', '==', true)
  );
  const [fitnessSnap, lekhiSnap] = await Promise.all([
    getCountFromServer(fitnessQuery),
    getCountFromServer(collection(db, 'lekhiDailyCompletions'))
  ]);
  return {
    fitnessDays: fitnessSnap.data().count,
    lekhiDays: lekhiSnap.data().count
  };
};

export const getSundayTestsThisWeekCount = async () => {
  const india = getIndiaDate();
  const dayOfWeek = india.getDay();
  const sunday = new Date(india);
  sunday.setDate(india.getDate() - dayOfWeek);
  const sundayStr = getIndiaDateString(sunday);
  const sundayStart = new Date(sundayStr + 'T00:00:00');

  const q = query(
    collection(db, 'lekhiTestResults'),
    where('submittedAt', '>=', sundayStart)
  );
  const snap = await getCountFromServer(q);
  return snap.data().count;
};

export const getNotificationMessages = async () => {
  const snapshot = await getDocs(collection(db, 'notificationMessages'));
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const saveNotificationMessage = async (id, message) => {
  if (id) {
    await updateDoc(doc(db, 'notificationMessages', id), { message, updatedAt: serverTimestamp() });
  } else {
    const ref = doc(collection(db, 'notificationMessages'));
    await setDoc(ref, { message, createdAt: serverTimestamp() });
  }
};

export const deleteNotificationMessage = async (id) => {
  await deleteDoc(doc(db, 'notificationMessages', id));
};

export const getFAQs = async () => {
  const snapshot = await getDocs(collection(db, 'faqs'));
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const saveFAQ = async (id, data) => {
  if (id) {
    await updateDoc(doc(db, 'faqs', id), { ...data, updatedAt: serverTimestamp() });
  } else {
    const ref = doc(collection(db, 'faqs'));
    await setDoc(ref, { ...data, createdAt: serverTimestamp() });
  }
};

export const deleteFAQ = async (id) => {
  await deleteDoc(doc(db, 'faqs', id));
};

export const getMotivationalMessages = async () => {
  const snapshot = await getDocs(collection(db, 'motivationalMessages'));
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const saveMotivationalMessage = async (id, data) => {
  if (id) {
    await updateDoc(doc(db, 'motivationalMessages', id), { ...data, updatedAt: serverTimestamp() });
  } else {
    const ref = doc(collection(db, 'motivationalMessages'));
    await setDoc(ref, { ...data, createdAt: serverTimestamp() });
  }
};

export const deleteMotivationalMessage = async (id) => {
  await deleteDoc(doc(db, 'motivationalMessages', id));
};

// ===== LEKHI CONTENT FUNCTIONS =====

export const LEKHI_TOPICS = {
  grammar: ['jati', 'vibhakti', 'samas', 'alankar', 'prayog', 'vachan', 'ling', 'kaal', 'samanarthi', 'virudharthi', 'vakyaprachar', 'shuddhalekhan', 'viramchinhe', 'sandhi', 'sadrushya'],
  math: ['simplification', 'lcm_hcf', 'percentages', 'profit_loss', 'si_ci', 'ratio', 'average', 'time_work', 'std', 'pipes', 'area', 'volume', 'geometry', 'squares_roots', 'divisibility'],
  reasoning: ['num_series', 'letter_series', 'coding_decoding', 'blood_relations', 'direction', 'classification', 'analogies', 'arrangement', 'word_arrangement', 'syllogism', 'mirror', 'ranking', 'di', 'non_verbal', 'problem_solving'],
  gk: ['maha_history', 'indian_history', 'maha_geography', 'indian_geography', 'polity', 'economy', 'physics', 'chemistry', 'biology', 'sports', 'personalities', 'awards', 'days_years', 'current_affairs_national', 'current_affairs_maharashtra']
};

const validateItemTopic = (item) => {
  const validTopics = LEKHI_TOPICS[item.subject];
  if (!validTopics) {
    throw new Error(`Invalid subject "${item.subject}". Expected: grammar, math, reasoning, gk.`);
  }
  if (!validTopics.includes(item.topic)) {
    throw new Error(`Invalid topic "${item.topic}" for subject "${item.subject}". Expected one of: ${validTopics.join(', ')}.`);
  }
};

const LEKHI_PRACTICE_MAP = {
  mcqs: 'lekhiPracticeMCQs',
  fillBlanks: 'lekhiFillBlanks',
  matchFollowing: 'lekhiMatchFollowing',
  flashcards: 'lekhiFlashcards'
};

const LEKHI_PRACTICE_COLLECTIONS = [
  'lekhiPracticeMCQs',
  'lekhiFillBlanks',
  'lekhiMatchFollowing',
  'lekhiFlashcards'
];

const writeLekhiItems = async (collectionName, items, extraFields = {}) => {
  const batch = writeBatch(db);
  items.forEach(item => {
    const ref = doc(collection(db, collectionName));
    batch.set(ref, {
      ...item,
      ...extraFields,
      createdAt: serverTimestamp()
    });
  });
  await batch.commit();
  return items.length;
};

export const uploadLekhiContent = async (parsed, type, testNumber = null) => {
  const counts = {};

  if (type === 'diagnostic') {
    if (!Array.isArray(parsed)) throw new Error('Diagnostic upload must be a JSON array.');
    parsed.forEach(validateItemTopic);
    counts.lekhiDiagnosticQuestions = await writeLekhiItems('lekhiDiagnosticQuestions', parsed);
    return counts;
  }

  if (type === 'test') {
    if (!Array.isArray(parsed)) throw new Error('Test upload must be a JSON array.');
    if (!testNumber) throw new Error('Test number is required for test uploads.');
    parsed.forEach(validateItemTopic);
    counts.lekhiTestQuestions = await writeLekhiItems(
      'lekhiTestQuestions',
      parsed,
      { testNumber: Number(testNumber) }
    );
    return counts;
  }

  if (type === 'practice') {
    if (Array.isArray(parsed) || typeof parsed !== 'object' || parsed === null) {
      throw new Error('Practice upload must be a JSON object with content-type keys.');
    }
    const keys = Object.keys(LEKHI_PRACTICE_MAP).filter(k => Array.isArray(parsed[k]));
    if (keys.length === 0) {
      throw new Error('No practice content found. Expected keys: mcqs, fillBlanks, matchFollowing, flashcards.');
    }
    for (const key of keys) {
      parsed[key].forEach(validateItemTopic);
    }
    for (const key of keys) {
      const collectionName = LEKHI_PRACTICE_MAP[key];
      counts[collectionName] = await writeLekhiItems(collectionName, parsed[key]);
    }
    return counts;
  }

  throw new Error('Unknown upload type.');
};

export const uploadLekhiLearnContent = async (item) => {
  validateItemTopic(item);

  if (item.type === 'text') {
    if (!item.text || !item.text.trim()) {
      throw new Error('Text content is empty.');
    }
    const docRef = doc(collection(db, 'lekhiLearnContent'));
    await setDoc(docRef, {
      subject: item.subject,
      topic: item.topic,
      level: item.level,
      type: 'text',
      text: item.text,
      createdAt: serverTimestamp()
    });
    return { type: 'text', uploaded: 1 };
  }

  if (item.type === 'audio') {
    if (!item.file) {
      throw new Error('No audio file provided.');
    }
    if (item.file.size > 10 * 1024 * 1024) {
      throw new Error(`File "${item.file.name}" exceeds 10 MB limit.`);
    }
    const safeName = item.file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `lekhi/audio/${item.subject}_${item.topic}_${item.level}_${Date.now()}_${safeName}`;
    const sRef = storageRef(storage, path);
    await uploadBytes(sRef, item.file);
    const url = await getDownloadURL(sRef);

    const docRef = doc(collection(db, 'lekhiLearnContent'));
    await setDoc(docRef, {
      subject: item.subject,
      topic: item.topic,
      level: item.level,
      type: 'audio',
      audioURL: url,
      storagePath: path,
      fileName: item.file.name,
      createdAt: serverTimestamp()
    });
    return { type: 'audio', uploaded: 1 };
  }

  throw new Error(`Unknown Learn type "${item.type}". Expected "text" or "audio".`);
};

const deleteByFilters = async (collectionName, filters) => {
  const constraints = Object.entries(filters).map(([field, value]) => where(field, '==', value));
  const snap = await getDocs(query(collection(db, collectionName), ...constraints));
  await Promise.all(snap.docs.map(d => deleteDoc(doc(db, collectionName, d.id))));
  return snap.size;
};

const deleteLekhiLearnSlice = async (subject, topic, level) => {
  const q = query(
    collection(db, 'lekhiLearnContent'),
    where('subject', '==', subject),
    where('topic', '==', topic),
    where('level', '==', level)
  );
  const snap = await getDocs(q);

  for (const docSnap of snap.docs) {
    const data = docSnap.data();
    if (data.type === 'audio' && data.storagePath) {
      try {
        await deleteObject(storageRef(storage, data.storagePath));
      } catch (e) {
        if (e.code !== 'storage/object-not-found') {
          throw e;
        }
      }
    }
    await deleteDoc(doc(db, 'lekhiLearnContent', docSnap.id));
  }
  return snap.size;
};

export const deleteLekhiContent = async (type, opts) => {
  const counts = {};

  if (type === 'diagnostic') {
    counts.lekhiDiagnosticQuestions = await deleteByFilters('lekhiDiagnosticQuestions', {
      subject: opts.subject,
      topic: opts.topic
    });
    return counts;
  }

  if (type === 'practice') {
    for (const collectionName of LEKHI_PRACTICE_COLLECTIONS) {
      counts[collectionName] = await deleteByFilters(collectionName, {
        subject: opts.subject,
        topic: opts.topic,
        level: opts.level
      });
    }
    return counts;
  }

  if (type === 'test') {
    counts.lekhiTestQuestions = await deleteByFilters('lekhiTestQuestions', {
      testNumber: Number(opts.testNumber),
      subject: opts.subject,
      level: opts.level
    });
    return counts;
  }

  if (type === 'learn') {
    counts.lekhiLearnContent = await deleteLekhiLearnSlice(opts.subject, opts.topic, opts.level);
    return counts;
  }

  throw new Error('Unknown delete type.');
};

// ===== LEKHI CONTENT VIEW =====

const LEKHI_SUBJECTS = ['grammar', 'math', 'reasoning', 'gk'];

export const getLekhiContentTotals = async () => {
  const collections = [
    { key: 'diagnostic', name: 'lekhiDiagnosticQuestions' },
    { key: 'practice_mcqs', name: 'lekhiPracticeMCQs' },
    { key: 'practice_fillBlanks', name: 'lekhiFillBlanks' },
    { key: 'practice_matchFollowing', name: 'lekhiMatchFollowing' },
    { key: 'practice_flashcards', name: 'lekhiFlashcards' },
    { key: 'test', name: 'lekhiTestQuestions' },
    { key: 'learn', name: 'lekhiLearnContent' }
  ];
  const result = {};
  await Promise.all(collections.map(async ({ key, name }) => {
    const snap = await getCountFromServer(collection(db, name));
    result[key] = snap.data().count;
  }));
  return result;
};

export const getLekhiContentBreakdown = async ({ type, subject, testNumber }) => {
  if (!LEKHI_SUBJECTS.includes(subject)) {
    throw new Error(`Invalid subject "${subject}".`);
  }
  if (type === 'test' && !testNumber) {
    throw new Error('Test number is required for test breakdown.');
  }

  const collectionMap = {
    diagnostic: 'lekhiDiagnosticQuestions',
    practice_mcqs: 'lekhiPracticeMCQs',
    practice_fillBlanks: 'lekhiFillBlanks',
    practice_matchFollowing: 'lekhiMatchFollowing',
    practice_flashcards: 'lekhiFlashcards',
    test: 'lekhiTestQuestions',
    learn: 'lekhiLearnContent'
  };
  const collectionName = collectionMap[type];
  if (!collectionName) throw new Error(`Invalid type "${type}".`);

  const filters = [where('subject', '==', subject)];
  if (type === 'test') filters.push(where('testNumber', '==', Number(testNumber)));

  const snap = await getDocs(query(collection(db, collectionName), ...filters));
  const docsList = snap.docs.map(d => ({ id: d.id, ...d.data() }));

  const validTopics = LEKHI_TOPICS[subject];
  const grid = {};
  validTopics.forEach(t => {
    grid[t] = { easy: 0, medium: 0, hard: 0 };
  });

  docsList.forEach(d => {
    const t = d.topic;
    const l = d.level || 'medium';
    if (!grid[t]) {
      grid[t] = { easy: 0, medium: 0, hard: 0, _invalid: true };
    }
    if (grid[t][l] === undefined) {
      grid[t][l] = 0;
    }
    grid[t][l]++;
  });

  return { grid, docs: docsList };
};

// ===== LEKHI DAILY PLANS =====

export const computeLekhiRotation = (dayNumber) => {
  const subjects = ['grammar', 'math', 'reasoning', 'gk'];
  const index = dayNumber - 1;
  const subjectIndex = index % 4;
  const topicIndex = Math.floor(index / 4) % 15;
  const subject = subjects[subjectIndex];
  const topic = LEKHI_TOPICS[subject][topicIndex];
  return { subject, topic, isRevision: dayNumber > 60 };
};

export const getLekhiDailyPlansRange = async (startDay, endDay) => {
  const days = [];
  for (let d = startDay; d <= endDay; d++) days.push(d);
  const results = await Promise.all(
    days.map(d => getDoc(doc(db, 'lekhiDailyPlans', `lekhiDay${d}`)))
  );
  return results.map((snap, i) => snap.exists() ? { day: days[i], ...snap.data() } : null);
};

export const getSavedLekhiDays = async () => {
  const snap = await getDocs(collection(db, 'lekhiDailyPlans'));
  return snap.docs
    .map(d => d.data().day)
    .filter(d => typeof d === 'number')
    .sort((a, b) => a - b);
};

export const getAvailableLekhiContentCounts = async () => {
  const collections = [
    { key: 'audio', name: 'lekhiLearnContent', filter: d => d.type === 'audio' },
    { key: 'text', name: 'lekhiLearnContent', filter: d => d.type === 'text' },
    { key: 'mcqs', name: 'lekhiPracticeMCQs', filter: null },
    { key: 'fillBlanks', name: 'lekhiFillBlanks', filter: null },
    { key: 'matchFollowing', name: 'lekhiMatchFollowing', filter: null },
    { key: 'flashcards', name: 'lekhiFlashcards', filter: null }
  ];

  const cache = {};
  const fetchOnce = async (name) => {
    if (!cache[name]) {
      const snap = await getDocs(collection(db, name));
      cache[name] = snap.docs.map(d => d.data());
    }
    return cache[name];
  };

  const counts = {};
  LEKHI_SUBJECTS.forEach(s => {
    counts[s] = {};
    LEKHI_TOPICS[s].forEach(t => {
      counts[s][t] = { easy: {}, medium: {}, hard: {} };
      ['easy', 'medium', 'hard'].forEach(l => {
        counts[s][t][l] = { audio: 0, text: 0, mcqs: 0, fillBlanks: 0, matchFollowing: 0, flashcards: 0 };
      });
    });
  });

  for (const { key, name, filter } of collections) {
    const docs = await fetchOnce(name);
    docs.forEach(d => {
      if (filter && !filter(d)) return;
      const s = d.subject, t = d.topic, l = d.level;
      if (counts[s] && counts[s][t] && counts[s][t][l]) {
        counts[s][t][l][key]++;
      }
    });
  }

  return counts;
};

export const saveLekhiDailyPlansBatch = async (plans) => {
  const batch = writeBatch(db);
  plans.forEach(plan => {
    const ref = doc(db, 'lekhiDailyPlans', `lekhiDay${plan.day}`);
    batch.set(ref, {
      ...plan,
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp()
    });
  });
  await batch.commit();
  return plans.length;
};

export const getLekhiDayPlan = async (dayNumber) => {
  const snap = await getDoc(doc(db, 'lekhiDailyPlans', `lekhiDay${dayNumber}`));
  if (snap.exists()) return { id: snap.id, ...snap.data() };
  return null;
};

// ===== LEKHI DIAGNOSTIC =====

const LEKHI_LEVEL_LABELS = {
  easy: 'प्रारंभ',
  medium: 'प्रगत',
  hard: 'प्रावीण्य'
};

const scoreToLevel = (score) => {
  if (score >= 7) return { level: 'hard', label: LEKHI_LEVEL_LABELS.hard };
  if (score >= 4) return { level: 'medium', label: LEKHI_LEVEL_LABELS.medium };
  return { level: 'easy', label: LEKHI_LEVEL_LABELS.easy };
};

const pickRandom = (arr, n) => {
  const copy = [...arr];
  const out = [];
  for (let i = 0; i < n && copy.length > 0; i++) {
    const idx = Math.floor(Math.random() * copy.length);
    out.push(copy.splice(idx, 1)[0]);
  }
  return out;
};

export const getDiagnosticQuestions = async () => {
  const subjects = ['grammar', 'math', 'reasoning', 'gk'];
  const result = {};

  await Promise.all(subjects.map(async (subject) => {
    const q = query(
      collection(db, 'lekhiDiagnosticQuestions'),
      where('subject', '==', subject)
    );
    const snap = await getDocs(q);
    const allQs = snap.docs.map(d => ({ id: d.id, ...d.data() }));

    const byTopic = {};
    allQs.forEach(q => {
      if (!byTopic[q.topic]) byTopic[q.topic] = [];
      byTopic[q.topic].push(q);
    });

    const topics = Object.keys(byTopic);
    const chosenTopics = pickRandom(topics, 8);
    result[subject] = chosenTopics.map(topic => {
      const qs = byTopic[topic];
      return qs[Math.floor(Math.random() * qs.length)];
    });
  }));

  return result;
};

export const submitDiagnostic = async (userId, questions, answers) => {
  const subjects = ['grammar', 'math', 'reasoning', 'gk'];
  const results = {};
  const userUpdate = {
    diagnosticCompleted: true,
    lekhiStartDate: serverTimestamp(),
    lekhiCurrentDay: 1,
    lekhiPoints: 0,
    lekhiTestsTaken: 0,
    lastActive: serverTimestamp()
  };

  let totalScore = 0;
  const diagnosticScores = {};

  subjects.forEach(subject => {
    const subjectQs = questions[subject] || [];
    const subjectAnswers = answers[subject] || {};
    let score = 0;
    subjectQs.forEach(q => {
      if (subjectAnswers[q.id] === q.correctAnswer) score++;
    });
    totalScore += score;

    const { level, label } = scoreToLevel(score);
    results[subject] = { score, total: subjectQs.length, level, label };

    userUpdate[`${subject}Level`] = level;
    userUpdate[`${subject}LevelChangedAt`] = serverTimestamp();
    diagnosticScores[subject] = score;
  });

  userUpdate.diagnosticScores = diagnosticScores;
  userUpdate.diagnosticTotalScore = totalScore;
  userUpdate.diagnosticTakenAt = serverTimestamp();

  await updateDoc(doc(db, 'users', userId), userUpdate);

  return { perSubject: results, totalScore, totalQuestions: 32 };
};

// ===== LEKHI DAILY SESSION =====

const parseAudioOrder = (fileName) => {
  if (!fileName) return null;
  const lower = fileName.toLowerCase();
  if (lower.includes('audio1')) return 1;
  if (lower.includes('audio2')) return 2;
  return null;
};

export const getLekhiSessionContent = async (subject, topic, level) => {
  const learnQuery = query(
    collection(db, 'lekhiLearnContent'),
    where('subject', '==', subject),
    where('topic', '==', topic),
    where('level', '==', level)
  );
  const mcqQuery = query(
    collection(db, 'lekhiPracticeMCQs'),
    where('subject', '==', subject),
    where('topic', '==', topic),
    where('level', '==', level)
  );
  const fillQuery = query(
    collection(db, 'lekhiFillBlanks'),
    where('subject', '==', subject),
    where('topic', '==', topic),
    where('level', '==', level)
  );
  const matchQuery = query(
    collection(db, 'lekhiMatchFollowing'),
    where('subject', '==', subject),
    where('topic', '==', topic),
    where('level', '==', level)
  );
  const flashQuery = query(
    collection(db, 'lekhiFlashcards'),
    where('subject', '==', subject),
    where('topic', '==', topic),
    where('level', '==', level)
  );

  const [learnSnap, mcqSnap, fillSnap, matchSnap, flashSnap] = await Promise.all([
    getDocs(learnQuery),
    getDocs(mcqQuery),
    getDocs(fillQuery),
    getDocs(matchQuery),
    getDocs(flashQuery)
  ]);

  const learnDocs = learnSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  const audios = learnDocs.filter(d => d.type === 'audio');
  const texts = learnDocs.filter(d => d.type === 'text');

  let audio1 = audios.find(a => parseAudioOrder(a.fileName) === 1);
  let audio2 = audios.find(a => parseAudioOrder(a.fileName) === 2);

  if (!audio1 || !audio2) {
    const sorted = [...audios].sort((a, b) => {
      const ta = a.createdAt?.seconds || 0;
      const tb = b.createdAt?.seconds || 0;
      return ta - tb;
    });
    audio1 = audio1 || sorted[0] || null;
    audio2 = audio2 || sorted.find(a => a !== audio1) || null;
  }

  return {
    audio1,
    audio2,
    text: texts[0] || null,
    mcqs: mcqSnap.docs.map(d => ({ id: d.id, ...d.data() })),
    fillBlanks: fillSnap.docs.map(d => ({ id: d.id, ...d.data() })),
    matchFollowing: matchSnap.docs.map(d => ({ id: d.id, ...d.data() })),
    flashcards: flashSnap.docs.map(d => ({ id: d.id, ...d.data() }))
  };
};

export const saveLekhiSession = async (userId, sessionData) => {
  const todayStr = getIndiaDateString();
  const docId = `${userId}_${todayStr}`;

  // Today's Lekhi is already recorded — award nothing and exit quietly.
  const existingCompletion = await getDoc(doc(db, 'lekhiDailyCompletions', docId));
  if (existingCompletion.exists()) return false;

  const userSnap = await getDoc(doc(db, 'users', userId));
  if (!userSnap.exists()) throw new Error('User not found.');
  const userData = userSnap.data();

  const batch = writeBatch(db);

  batch.set(doc(db, 'lekhiDailyActivity', docId), {
    userId,
    date: todayStr,
    dayNumber: sessionData.dayNumber,
    subjectCode: sessionData.subject,
    topicCode: sessionData.topic,
    level: sessionData.level,
    learnTimeSeconds: sessionData.learnTimeSeconds || 0,
    practiceTimeSeconds: sessionData.practiceTimeSeconds || 780,
    totalQuestions: sessionData.totalQuestions || 13,
    correctCount: sessionData.correctCount || 0,
    wrongCount: sessionData.wrongCount || 0,
    wrongItemIds: sessionData.wrongItemIds || [],
    completed: true,
    sessionAbandoned: false,
    completedAt: serverTimestamp()
  });

  batch.set(doc(db, 'lekhiDailyCompletions', docId), {
    userId,
    date: todayStr,
    dayNumber: sessionData.dayNumber,
    completedAt: serverTimestamp()
  });

  batch.update(doc(db, 'users', userId), {
    lekhiPoints: (userData.lekhiPoints || 0) + 1,
    lekhiCurrentDay: (userData.lekhiCurrentDay || 1) + 1,
    lastLekhiCompletedDate: todayStr
  });

  await batch.commit();

  await updateVillagePointsForUser(userData.village, userData.district, 1);

  await recordDailyActivity(userId, 'lekhi');

  return true;
};

// ===== LEKHI LIBRARY =====

export const getLekhiLibraryContent = async (userId) => {
  const activitySnap = await getDocs(query(
    collection(db, 'lekhiDailyActivity'),
    where('userId', '==', userId),
    where('completed', '==', true)
  ));

  const consumed = {};
  activitySnap.docs.forEach(d => {
    const data = d.data();
    const key = `${data.subjectCode}|${data.topicCode}|${data.level}`;
    if (!consumed[key]) {
      consumed[key] = {
        subject: data.subjectCode,
        topic: data.topicCode,
        level: data.level
      };
    }
  });

  const slices = Object.values(consumed);
  if (slices.length === 0) return { grammar: {}, math: {}, reasoning: {}, gk: {} };

  const learnDocsBySlice = await Promise.all(slices.map(async (slice) => {
    const q = query(
      collection(db, 'lekhiLearnContent'),
      where('subject', '==', slice.subject),
      where('topic', '==', slice.topic),
      where('level', '==', slice.level)
    );
    const snap = await getDocs(q);
    return {
      ...slice,
      items: snap.docs.map(d => ({ id: d.id, ...d.data() }))
    };
  }));

  const library = { grammar: {}, math: {}, reasoning: {}, gk: {} };
  learnDocsBySlice.forEach(slice => {
    if (!library[slice.subject]) library[slice.subject] = {};
    if (!library[slice.subject][slice.topic]) library[slice.subject][slice.topic] = {};
    library[slice.subject][slice.topic][slice.level] = slice.items;
  });

  return library;
};

// ===== LEKHI SUNDAY TEST =====

export const getSundayTestQuestions = async (testNumber, userLevels) => {
  const q = query(
    collection(db, 'lekhiTestQuestions'),
    where('testNumber', '==', Number(testNumber))
  );
  const snap = await getDocs(q);
  const allQs = snap.docs.map(d => ({ id: d.id, ...d.data() }));

  const filtered = allQs.filter(qDoc => {
    const subjectLevel = userLevels[qDoc.subject];
    return qDoc.level === subjectLevel;
  });

  // Serve every matching question — multiple questions per topic allowed.
  // Stable order: subject, then topic.
  filtered.sort((a, b) =>
    a.subject === b.subject
      ? (a.topic || '').localeCompare(b.topic || '')
      : (a.subject || '').localeCompare(b.subject || '')
  );

  return filtered;
};

export const submitSundayTest = async (userId, testNumber, questions, answers) => {
  const subjectScores = { grammar: 0, math: 0, reasoning: 0, gk: 0 };
  const questionLevelData = [];
  let totalScore = 0;

  questions.forEach(qDoc => {
    const userAnswer = answers[qDoc.id];
    const isCorrect = userAnswer === qDoc.correctAnswer;
    if (isCorrect) {
      subjectScores[qDoc.subject] = (subjectScores[qDoc.subject] || 0) + 1;
      totalScore++;
    }
    questionLevelData.push({
      qId: qDoc.id,
      subject: qDoc.subject,
      topic: qDoc.topic,
      level: qDoc.level,
      userAnswer: userAnswer || null,
      correct: isCorrect
    });
  });

  const docId = `${userId}_test${testNumber}`;
  await setDoc(doc(db, 'lekhiTestResults', docId), {
    userId,
    testNumber: Number(testNumber),
    score: totalScore,
    subjectScores,
    questionLevelData,
    submittedAt: serverTimestamp()
  });

  const userSnap = await getDoc(doc(db, 'users', userId));
  if (userSnap.exists()) {
    const userData = userSnap.data();
    await updateDoc(doc(db, 'users', userId), {
      lekhiPoints: (userData.lekhiPoints || 0) + 2,
      lekhiTestsTaken: (userData.lekhiTestsTaken || 0) + 1,
      lastActive: serverTimestamp()
    });
    await updateVillagePointsForUser(userData.village, userData.district, 2);
    await recordDailyActivity(userId, 'lekhi');
  }

  return { totalScore, subjectScores, questionLevelData };
};


// ===== SUNDAY TEST PROGRESS & RESUME =====

export const isLekhiTestDay = (userData) => {
  if (!userData?.lekhiStartDate) return false;
  if (userData.diagnosticCompleted !== true) return false;

  const india = getIndiaDate();
  if (india.getDay() !== 0) return false;

  const startDate = userData.lekhiStartDate.seconds
    ? new Date(userData.lekhiStartDate.seconds * 1000)
    : new Date(userData.lekhiStartDate);

  const startIndia = new Date(startDate.getTime() + (startDate.getTimezoneOffset() * 60000) + (5.5 * 60 * 60 * 1000));
  const startStr = getIndiaDateString(startIndia);
  const todayStr = getIndiaDateString(india);

  if (todayStr <= startStr) return false;

  return true;
};

export const getNextSundayTestNumber = async (userId) => {
  const resultsSnap = await getDocs(
    query(collection(db, 'lekhiTestResults'), where('userId', '==', userId))
  );
  const takenNumbers = resultsSnap.docs
    .map(d => d.data().testNumber)
    .filter(n => typeof n === 'number');
  const highest = takenNumbers.length > 0 ? Math.max(...takenNumbers) : 0;
  return highest + 1;
};

export const hasUserTakenTest = async (userId, testNumber) => {
  const docId = `${userId}_test${testNumber}`;
  const snap = await getDoc(doc(db, 'lekhiTestResults', docId));
  return snap.exists();
};

export const getOrCreateTestProgress = async (userId, testNumber, questions) => {
  const docId = `${userId}_test${testNumber}`;
  const progressRef = doc(db, 'lekhiTestProgress', docId);
  const existing = await getDoc(progressRef);

  if (existing.exists()) {
    const data = existing.data();
    const now = Date.now();
    const endsAtMs = data.endsAt?.seconds ? data.endsAt.seconds * 1000 : 0;
    const isExpired = endsAtMs > 0 && now > endsAtMs;

    if (!data.submitted && !isExpired) {
      return { progressDoc: data, isResume: true, expired: false };
    }
    if (!data.submitted && isExpired) {
      return { progressDoc: data, isResume: false, expired: true };
    }
    return { progressDoc: data, isResume: false, expired: false };
  }

  const startMs = Date.now();
  const endMs = startMs + 60 * 60 * 1000;
  const newData = {
    userId,
    testNumber: Number(testNumber),
    startedAt: new Date(startMs),
    endsAt: new Date(endMs),
    questionIds: questions.map(q => q.id),
    answers: {},
    submitted: false,
    createdAt: serverTimestamp()
  };
  await setDoc(progressRef, newData);
  return { progressDoc: newData, isResume: false, expired: false };
};

export const saveTestAnswer = async (userId, testNumber, qId, answer) => {
  const docId = `${userId}_test${testNumber}`;
  const progressRef = doc(db, 'lekhiTestProgress', docId);
  await updateDoc(progressRef, {
    [`answers.${qId}`]: answer
  });
};

export const finalizeSundayTest = async (userId, testNumber, questions) => {
  const docId = `${userId}_test${testNumber}`;
  const progressRef = doc(db, 'lekhiTestProgress', docId);
  const progressSnap = await getDoc(progressRef);
  if (!progressSnap.exists()) {
    throw new Error('Test progress not found.');
  }
  const progressData = progressSnap.data();
  if (progressData.submitted) {
    throw new Error('Test already submitted.');
  }

  const answers = progressData.answers || {};
  const result = await submitSundayTest(userId, testNumber, questions, answers);

  await updateDoc(progressRef, {
    submitted: true,
    submittedAt: serverTimestamp()
  });

  return result;
};


// ===== ADMIN OVERRIDES =====

export const unlockFitnessForUser = async (userId) => {
  await updateDoc(doc(db, 'users', userId), {
    fitnessOverrideUnlocked: true,
    lastActive: serverTimestamp()
  });
};

export const unlockLekhiForUser = async (userId) => {
  await updateDoc(doc(db, 'users', userId), {
    lekhiOverrideUnlocked: true,
    diagnosticCompleted: true,
    grammarLevel: 'easy',
    mathLevel: 'easy',
    reasoningLevel: 'easy',
    gkLevel: 'easy',
    grammarLevelChangedAt: serverTimestamp(),
    mathLevelChangedAt: serverTimestamp(),
    reasoningLevelChangedAt: serverTimestamp(),
    gkLevelChangedAt: serverTimestamp(),
    lekhiStartDate: serverTimestamp(),
    lekhiCurrentDay: 1,
    lekhiPoints: 0,
    lekhiTestsTaken: 0,
    lastActive: serverTimestamp()
  });
};

export const pauseUserStreak = async (userId, userName, reason, adminId) => {
  const todayStr = getIndiaDateString();
  const logId = `${userId}_${todayStr}`;

  const existing = await getDoc(doc(db, 'streakPauseLogs', logId));
  if (existing.exists()) {
    throw new Error('Streak already paused for this user today.');
  }

  await setDoc(doc(db, 'streakPauseLogs', logId), {
    userId,
    userName: userName || '',
    pausedDate: todayStr,
    reason: reason || '',
    adminId: adminId || '',
    timestamp: serverTimestamp()
  });

  const userSnap = await getDoc(doc(db, 'users', userId));
  if (userSnap.exists()) {
    const userData = userSnap.data();
    const existingDates = userData.streakPausedDates || [];
    if (!existingDates.includes(todayStr)) {
      await updateDoc(doc(db, 'users', userId), {
        streakPausedDates: [...existingDates, todayStr]
      });
    }
  }

  return { paused: true, date: todayStr };
};

export const getStreakPauseLogs = async () => {
  const snap = await getDocs(collection(db, 'streakPauseLogs'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const isStreakPausedToday = async (userId) => {
  const todayStr = getIndiaDateString();
  const snap = await getDoc(doc(db, 'streakPauseLogs', `${userId}_${todayStr}`));
  return snap.exists();
};

// ===== FITNESS LIBRARY =====

export const getFitnessLibraryTasks = async () => {
  const snap = await getDocs(collection(db, 'fitnessTasks'));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(t => t.youtubeURL && t.youtubeURL.trim() !== '');
};

// ===== PROGRESS PAGE — LEKHI HELPERS =====

export const getLekhiCompletionDates = async (userId) => {
  const q = query(collection(db, 'lekhiDailyCompletions'), where('userId', '==', userId));
  const snap = await getDocs(q);
  return snap.docs.map(d => d.data().date).filter(Boolean);
};

export const getLekhiActivityForUser = async (userId) => {
  const q = query(
    collection(db, 'lekhiDailyActivity'),
    where('userId', '==', userId),
    where('completed', '==', true)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getLekhiTestResultsForUser = async (userId) => {
  const q = query(collection(db, 'lekhiTestResults'), where('userId', '==', userId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getLekhiTestQuestionsByNumber = async (testNumber) => {
  const q = query(collection(db, 'lekhiTestQuestions'), where('testNumber', '==', Number(testNumber)));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};